import {Injectable} from '@angular/core';

import {DashboardMapProperties} from '@models/dashboard/dashboard-map-properties';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {TRAFFIC_LIGHT_STATUS_CLASS} from '@constants/machine/traffic-light/traffic-light-status';
import {GridAlgorithm, Cluster} from '@googlemaps/markerclusterer';

@Injectable({
  providedIn: 'root'
})
export class MarkerClustererService {

  private geocoderResponseCache = new Map();
  private static imagePath = 'assets/img/common/map/';

  public defineStatusIcon(trafficLight: TRAFFIC_LIGHT_STATUS_CLASS): string {
    switch (trafficLight) {
      case TRAFFIC_LIGHT_STATUS_CLASS.FAILURE:
      case TRAFFIC_LIGHT_STATUS_CLASS.OFFLINE:
        return 'fa-triangle-exclamation text-danger';

      case TRAFFIC_LIGHT_STATUS_CLASS.RUNNING:
        return 'fa-tools text-debug';

      case TRAFFIC_LIGHT_STATUS_CLASS.INFO:
        return 'fa-circle-info text-info';

      case TRAFFIC_LIGHT_STATUS_CLASS.WARNING:
        return 'fa-circle-exclamation text-warning';

      default:
        return 'fa-ban';
    }
  }

  public getClusterAlgorithm(): GridAlgorithm {
    return new GridAlgorithm({gridSize: 40});
  }

  public getClusterRenderer(): any {
    const clusterStyles = [
      MarkerClustererService.imagePath + 'map_cluster_light.svg',
      MarkerClustererService.imagePath + 'map_cluster_success.svg',
      MarkerClustererService.imagePath + 'map_cluster_info.svg',
      MarkerClustererService.imagePath + 'map_cluster_warning.svg',
      MarkerClustererService.imagePath + 'map_cluster_danger.svg',
    ];
    const determineIndex = this.determineIndex.bind(this);

    return {
      render: ({count, position, markers}: Cluster) => {
        let styleIndex = 1;
        if (markers) {
          markers.forEach((item: any) => {
            const trafficLight: TRAFFIC_LIGHT_STATUS_CLASS = item.get ? item.get('trafficLight') : undefined;
            styleIndex = determineIndex(styleIndex, trafficLight);
          });
        }

        const svgUrl = clusterStyles[styleIndex - 1] || clusterStyles[0];

        return new google.maps.Marker({
          position,
          icon: {
            url: svgUrl,
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 20),
          },
          label: {
            text: String(count),
            color: '#fff',
            fontWeight: '900',
            fontSize: '12px',
          },
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      }
    };
  }

  public setMarkers(dashboardMapProperties: DashboardMapProperties, machines: DashboardMachine[] | undefined) {
    dashboardMapProperties.markers = [];
    if (machines && machines.length > 0) {
      machines.forEach((machine) => {
        const coordinatesAreAvailable = MarkerClustererService.coordinatesAreAvailable(machine);
        if (MarkerClustererService.locationIsNotAvailable(machine.location) && !coordinatesAreAvailable) {
          return;
        }

        if (coordinatesAreAvailable) {
          const position = {lat: machine.locationDetails?.latitude, lng: machine.locationDetails?.longitude};
          MarkerClustererService.setMarker(dashboardMapProperties, position, machine);
        } else {
          this.getCoordinatesAndSetMarker(dashboardMapProperties, machine);
        }
      });
    }
  }

  private static setMarker(dashboardMapProperties: DashboardMapProperties, position: any, machine: DashboardMachine): void {
    const marker = new google.maps.Marker({
      position: position,
      label: machine.type,
    });
    marker.set('options', MarkerClustererService.setMarkerOptions(machine));

    dashboardMapProperties.markers.push(marker);
  }

  private static locationIsNotAvailable(location: string | undefined | null): boolean {
    return !location || location === ', ';
  }

  private static coordinatesAreAvailable(machine: DashboardMachine): boolean {
    return !!(machine.locationDetails && machine.locationDetails.latitude && machine.locationDetails.longitude);
  }

  private getCoordinatesAndSetMarker(dashboardMapProperties: DashboardMapProperties, machine: DashboardMachine): Promise<google.maps.GeocoderResponse> | void {
    const geocoderRequest = {
      address: machine.location,
    };

    const positionFromCache = this.geocoderResponseCache.get(geocoderRequest);
    if (positionFromCache) {
      MarkerClustererService.setMarker(dashboardMapProperties, positionFromCache, machine);
    } else {
      return new google.maps.Geocoder().geocode(geocoderRequest, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0 &&
            results[0].geometry.location.lat() && results[0].geometry.location.lng()) {
          const position = {lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng()};
          this.geocoderResponseCache.set(geocoderRequest, position);
          MarkerClustererService.setMarker(dashboardMapProperties, position, machine);
        } else {
          console.warn('geocode failed with', status);
        }
      });
    }
  }

  private static setMarkerOptions(machine: DashboardMachine): google.maps.MarkerOptions {
    if (!machine.type || machine.type.length === 0) {
      machine.type = ' ';
    }

    const icon: google.maps.Icon = {
      url: this.getColorOfPin(machine?.trafficLightClass),
      scaledSize: {
        height: 42,
        width: 42,
        equals: () => true
      },
    };

    return {
      draggable: false,
      animation: google.maps.Animation.DROP,
      title: machine.type,
      opacity: 1,
      machineId: machine.id,
      trafficLight: machine?.trafficLightClass,
      icon,
      label: {
        text: ' ',
        color: 'var(--primary)',
        fontSize: '16px',
        fontWeight: 'bold'
      }
    } as google.maps.MarkerOptions;
  }

  private static getColorOfPin(status: string | undefined): string {
    switch (status) {
      case TRAFFIC_LIGHT_STATUS_CLASS.FAILURE:
      case TRAFFIC_LIGHT_STATUS_CLASS.OFFLINE:
        return MarkerClustererService.imagePath + 'map_pin_danger.svg';

      case TRAFFIC_LIGHT_STATUS_CLASS.RUNNING:
        return MarkerClustererService.imagePath + 'map_pin_success.svg';

      case TRAFFIC_LIGHT_STATUS_CLASS.INFO:
        return MarkerClustererService.imagePath + 'map_pin_info.svg';

      case TRAFFIC_LIGHT_STATUS_CLASS.WARNING:
        return MarkerClustererService.imagePath + 'map_pin_warning.svg';

      default:
        return MarkerClustererService.imagePath + 'map_pin_light.svg';
    }
  }

  private determineIndex(oldIndex: number, trafficLight: TRAFFIC_LIGHT_STATUS_CLASS): number {
    let newStyleIndex: number;

    switch (trafficLight) {
      case TRAFFIC_LIGHT_STATUS_CLASS.RUNNING:
        newStyleIndex = 2;
        break;

      case TRAFFIC_LIGHT_STATUS_CLASS.INFO:
        newStyleIndex = 3;
        break;

      case TRAFFIC_LIGHT_STATUS_CLASS.WARNING:
        newStyleIndex = 4;
        break;

      case TRAFFIC_LIGHT_STATUS_CLASS.FAILURE:
      case TRAFFIC_LIGHT_STATUS_CLASS.OFFLINE:
        newStyleIndex = 5;
        break;

      default:
        newStyleIndex = 1;
    }

    if (oldIndex < newStyleIndex) {
      return newStyleIndex;
    }
    return oldIndex;
  }
}
